import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Search, Pencil, Trash2, Eye, FileText, Sparkles, Loader2 } from 'lucide-react';
import { adminGetGuides, adminDeleteGuide, adminGetCategories, aiBatchGenerate, adminCreateGuide } from '../lib/api';
import OpsLayout from '../components/OpsLayout';
import { Textarea } from '../components/ui/textarea';

export default function AdminGuideList() {
  const navigate = useNavigate();
  const [guides, setGuides] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ category: '', status: '', q: '' });
  const [showBatch, setShowBatch] = useState(false);
  const [batchTopics, setBatchTopics] = useState('');
  const [batchCategory, setBatchCategory] = useState('');
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchResults, setBatchResults] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) navigate('/ops/login');
  }, [navigate]);

  const fetchGuides = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.category) params.category = filters.category;
      if (filters.status) params.status = filters.status;
      if (filters.q) params.q = filters.q;
      const r = await adminGetGuides(params);
      setGuides(r.data.guides || []);
      setCategories(r.data.categories || []);
    } catch (e) {
      if (e.response?.status === 401) navigate('/ops/login');
    }
    setLoading(false);
  }, [filters, navigate]);

  useEffect(() => { fetchGuides(); }, [fetchGuides]);

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? This cannot be undone.`)) return;
    try {
      await adminDeleteGuide(slug);
      fetchGuides();
    } catch (e) {
      alert('Failed to delete: ' + (e.response?.data?.detail || e.message));
    }
  };

  const published = guides.filter(g => g.status === 'published').length;
  const drafts = guides.filter(g => g.status === 'draft').length;

  return (
    <OpsLayout>
    <div className="min-h-screen bg-slate-50" data-testid="admin-guide-list">
      <div className="bg-white border-b border-slate-200 px-6 md:px-12 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Instrument Serif', serif" }}>Content CMS</h1>
            <p className="text-xs text-slate-400 mt-0.5">Manage SEO pages and content silos</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setShowBatch(!showBatch)} variant="outline" className="rounded-full h-9 px-5 text-xs font-medium gap-1.5 border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="batch-generate-btn">
              <Sparkles className="w-3.5 h-3.5" /> AI Batch
            </Button>
            <Link to="/ops/guides/new">
              <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full h-9 px-5 text-xs font-medium gap-1.5" data-testid="create-guide-btn">
                <Plus className="w-3.5 h-3.5" /> New Page
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Batch AI Generation Panel */}
        {showBatch && (
          <div className="bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200/60 rounded-xl p-6 mb-8">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-violet-600" />
              <p className="text-sm font-semibold text-slate-900">AI Batch Page Generator</p>
              <span className="text-[10px] text-violet-500">GPT-5.2</span>
            </div>
            <p className="text-xs text-slate-500 mb-3">Enter topics (one per line). AI will generate page outlines for each — title, subtitle, meta, slug, and section structure.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Textarea placeholder={"Burlington vs Oakville for families\nBest coffee shops in Burlington\nIs Burlington safe?\nBurlington waterfront condos guide"} className="text-sm rounded-lg min-h-[120px] bg-white border-violet-200" value={batchTopics} onChange={e => setBatchTopics(e.target.value)} />
                <div className="flex items-center gap-2 mt-2">
                  <Select value={batchCategory || "general"} onValueChange={setBatchCategory}>
                    <SelectTrigger className="flex-1 h-8 text-xs rounded-lg bg-white border-violet-200"><SelectValue placeholder="Category" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      {categories.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button onClick={async () => {
                    const topics = batchTopics.split('\n').map(t => t.trim()).filter(Boolean);
                    if (!topics.length) return;
                    setBatchLoading(true); setBatchResults(null);
                    try {
                      const r = await aiBatchGenerate({ topics, category: batchCategory });
                      if (r.data.success) setBatchResults(r.data.pages);
                      else alert('Could not parse batch results.');
                    } catch (e) { alert('Batch failed: ' + (e.response?.data?.detail || e.message)); }
                    setBatchLoading(false);
                  }} disabled={batchLoading || !batchTopics.trim()} className="bg-violet-600 text-white hover:bg-violet-700 rounded-lg h-8 px-4 text-xs font-medium gap-1.5">
                    {batchLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Generate {batchTopics.split('\n').filter(t => t.trim()).length} Pages
                  </Button>
                </div>
              </div>
              <div>
                {batchResults ? (
                  <div className="space-y-2 max-h-[180px] overflow-y-auto">
                    {batchResults.map((page, i) => (
                      <div key={i} className="bg-white border border-violet-100 rounded-lg p-3 flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-900 truncate">{page.title}</p>
                          <p className="text-[10px] text-slate-400 font-mono">/{page.slug}</p>
                          <p className="text-[10px] text-slate-500 truncate">{page.subtitle}</p>
                        </div>
                        <Button size="sm" onClick={async () => {
                          try {
                            const cat = categories.find(c => c.slug === batchCategory);
                            await adminCreateGuide({ ...page, category_slug: batchCategory || 'insights', category: cat?.name || 'Neighbourhood Insights', status: 'draft', related_pages: [], related_neighbourhoods: [] });
                            fetchGuides();
                          } catch (e) { alert('Create failed: ' + (e.response?.data?.detail || e.message)); }
                        }} className="rounded-lg h-7 px-3 text-[10px] bg-emerald-600 text-white hover:bg-emerald-700 flex-shrink-0 ml-2">
                          Create
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : batchLoading ? (
                  <div className="flex items-center justify-center h-full text-violet-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
                ) : (
                  <div className="flex items-center justify-center h-full text-xs text-slate-400">Generated page outlines will appear here</div>
                )}
              </div>
            </div>
          </div>
        )}
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8" data-testid="guide-stats">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs text-slate-400 uppercase tracking-wider">Total Pages</p>
            <p className="font-mono text-2xl font-semibold mt-1">{guides.length}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs text-slate-400 uppercase tracking-wider">Published</p>
            <p className="font-mono text-2xl font-semibold mt-1 text-emerald-700">{published}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs text-slate-400 uppercase tracking-wider">Drafts</p>
            <p className="font-mono text-2xl font-semibold mt-1 text-amber-700">{drafts}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6 items-center" data-testid="guide-filters">
          <Select value={filters.category || "all"} onValueChange={(v) => setFilters(f => ({ ...f, category: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-[180px] bg-white rounded-lg h-9 text-sm" data-testid="filter-category">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={filters.status || "all"} onValueChange={(v) => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-[140px] bg-white rounded-lg h-9 text-sm" data-testid="filter-status">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
            </SelectContent>
          </Select>

          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search pages..."
              className="pl-9 bg-white rounded-lg h-9 text-sm"
              value={filters.q}
              onChange={(e) => setFilters(f => ({ ...f, q: e.target.value }))}
              data-testid="filter-search"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="guides-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs font-medium">Title</TableHead>
                <TableHead className="text-xs font-medium">Category</TableHead>
                <TableHead className="text-xs font-medium">Status</TableHead>
                <TableHead className="text-xs font-medium">Sections</TableHead>
                <TableHead className="text-xs font-medium">Links</TableHead>
                <TableHead className="text-xs font-medium w-32">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">Loading...</TableCell></TableRow>
              ) : guides.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">No pages found.</TableCell></TableRow>
              ) : guides.map((g) => (
                <TableRow key={g.slug} className="hover:bg-slate-50" data-testid={`guide-row-${g.slug}`}>
                  <TableCell>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{g.title}</p>
                      <p className="text-[11px] text-slate-400 font-mono">/{g.slug}</p>
                    </div>
                  </TableCell>
                  <TableCell><span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{g.category}</span></TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${g.status === 'published' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                      {g.status}
                    </span>
                  </TableCell>
                  <TableCell><span className="font-mono text-xs text-slate-400">{g.sections?.length || 0}</span></TableCell>
                  <TableCell><span className="font-mono text-xs text-slate-400">{(g.related_pages?.length || 0) + (g.related_neighbourhoods?.length || 0)}</span></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Link to={`/guides/${g.slug}`} target="_blank">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0"><Eye className="w-3.5 h-3.5 text-slate-400" /></Button>
                      </Link>
                      <Link to={`/ops/guides/edit/${g.slug}`}>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`edit-guide-${g.slug}`}><Pencil className="w-3.5 h-3.5 text-slate-400" /></Button>
                      </Link>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDelete(g.slug)} data-testid={`delete-guide-${g.slug}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Categories Management */}
        <div className="mt-12">
          <h2 className="text-xl tracking-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>Categories (Silos)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="categories-grid">
            {categories.map(cat => (
              <div key={cat.slug} className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-slate-900">{cat.name}</p>
                  <span className="font-mono text-xs text-slate-400">{cat.page_count || guides.filter(g => g.category_slug === cat.slug).length} pages</span>
                </div>
                <p className="text-[11px] text-slate-500 mb-1">{cat.description}</p>
                <p className="text-[10px] font-mono text-slate-400">/{cat.slug}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
    </OpsLayout>
  );
}
